import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import TechTaskProgressBar from '@/pages/ipe/workbench/components/TechTaskProgressBar';
import { getCropTaskCompletionDisplay } from '@/pages/ipe/workbench/utils/taskProgress';
import { sanitizeIpeUserMessage } from '@/pages/ipe/workbench/utils/errorHandler';
import {
  buildImageCropDetailInfoItems,
  buildImageCropDetectLogKey,
  logImageCropDetectInfo,
} from '../utils/imageCropDetailInfo';
import '@/styles/ipe/workbench/components/TechTaskProgressBar.css';
import '../styles/ImageCropInfoCell.css';

function formatCropCreatedAt(createdAt) {
  if (!createdAt) return '—';
  if (typeof createdAt === 'string' && createdAt.includes('T')) {
    return format(new Date(createdAt), 'yyyy-MM-dd HH:mm:ss');
  }
  return createdAt;
}

function ImageCropDetailRowInfoCell({ row, onRetry, retrying = false }) {
  const { t } = useTranslation('workbench');
  const loggedDetectKeysRef = useRef(new Set());

  useEffect(() => {
    const status = row?.status;
    if (!row || status === 'pending' || status === 'processing') return;
    const logKey = buildImageCropDetectLogKey(row);
    if (!logKey || loggedDetectKeysRef.current.has(logKey)) return;
    loggedDetectKeysRef.current.add(logKey);
    logImageCropDetectInfo(row);
  }, [row]);

  const infoItems = buildImageCropDetailInfoItems(row);
  const isFailed = row?.status === 'failed';
  const errorText = isFailed
    ? sanitizeIpeUserMessage(row?.error, t('taskProgress.failedGeneric'))
    : null;

  if (infoItems.length === 0 && !isFailed) {
    return null;
  }

  return (
    <div className="image-crop-detail-info-cell">
      {infoItems.length > 0 && (
        <div className="image-crop-detail-info-cell__meta">
          {infoItems.map((item) => (
            item.key === 'fallback' ? (
              <span key={item.key} className="image-crop-detail-info-cell__fallback">
                {item.value}
              </span>
            ) : (
              <span key={item.key} className="image-crop-detail-info-cell__item">
                <span className="image-crop-detail-info-cell__label">{item.label}</span>
                <span className="image-crop-detail-info-cell__value">{item.value}</span>
              </span>
            )
          ))}
        </div>
      )}

      {isFailed && (
        <div className="image-crop-detail-info-cell__error-row">
          <p className="image-crop-detail-info-cell__error-text">{errorText}</p>
          {typeof onRetry === 'function' && (
            <button
              type="button"
              className="image-crop-detail-info-cell__retry-btn"
              disabled={retrying}
              onClick={() => onRetry(row)}
            >
              {retrying ? t('studioChrome.retrying') : t('studioChrome.retry')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function ImageCropInfoCell({ task, row, onRetry, retrying }) {
  const { t } = useTranslation('workbench');
  if (row) {
    return (
      <ImageCropDetailRowInfoCell
        row={row}
        onRetry={onRetry}
        retrying={Boolean(retrying)}
      />
    );
  }

  const completion = getCropTaskCompletionDisplay(task);
  const percentDisplay = Math.round(completion.percent ?? 0);

  const rawOperator = task.operator || task.createdBy || task.creator || '';
  const operatorName = String(rawOperator || '').trim() || t('studioChrome.system');

  return (
    <div className="crop-task-info-cell">
      <div className="crop-task-info-meta crop-task-info-meta--stacked">
        <span className="crop-task-info-meta__item">
          <span className="crop-task-info-label">{t('studioChrome.timeLabel')}</span>
          <span className="crop-task-info-value crop-task-info-value--mono">
            {formatCropCreatedAt(task.createdAt)}
          </span>
        </span>
        <span className="crop-task-info-meta__item">
          <span className="crop-task-info-label">{t('studioChrome.operatorLabel')}</span>
          <span className="crop-task-info-value">{operatorName}</span>
        </span>
      </div>

      {completion.variant === 'success' ? (
        <div className="crop-task-complete-summary">
          {completion.total > 0 ? (
            <span className="crop-task-complete-summary__detail">
              <span className="crop-task-complete-summary__detail--done">{t('studioChrome.allDone')}</span>
              <span className="crop-task-complete-summary__detail--count">
                {` · ${t('studioChrome.countLine', {
                  count: Number(completion.total).toLocaleString(),
                  unit: t('studioChrome.unitSheet'),
                })}`}
              </span>
            </span>
          ) : (
            <span className="crop-task-complete-summary__detail crop-task-complete-summary__detail--done">
              {t('studioChrome.allDone')}
            </span>
          )}
        </div>
      ) : completion.showBar ? (
        <div className="crop-task-progress-block">
          <div className="crop-task-status-header">
            <span
              className={`crop-task-status-dot crop-task-status-dot--${completion.variant}`}
              aria-hidden
            />
            <span className={`crop-task-status-text crop-task-status-text--${completion.variant}`}>
              {completion.statusLabel}
            </span>
          </div>
          <TechTaskProgressBar percent={completion.percent} variant={completion.variant} />
          <div className="crop-task-progress-foot">
            <span className="crop-task-progress-foot__left">{completion.detailLeft}</span>
            <span className={`crop-task-progress-foot__pct crop-task-progress-foot__pct--${completion.variant}`}>
              {percentDisplay}%
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
